import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'

interface WorkspaceCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (workspaceData: { name: string; sortKey?: number }) => void
  isLoading?: boolean
}

export default function WorkspaceCreateModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading = false 
}: WorkspaceCreateModalProps) {
  const [workspaceName, setWorkspaceName] = useState('')
  const [sortKey, setSortKey] = useState<string>('')

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkspaceName('')
      setSortKey('')
    }
  }, [isOpen])

  const handleSubmit = () => {
    if (workspaceName.trim()) {
      const sortKeyNumber = sortKey.trim() ? parseInt(sortKey, 10) : undefined
      onSubmit({
        name: workspaceName.trim(),
        sortKey: !isNaN(sortKeyNumber as number) ? sortKeyNumber : undefined
      })
    }
  }

  const handleClose = () => {
    setWorkspaceName('')
    setSortKey('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{ 
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2 
              className="font-bold"
              style={{ 
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              워크스페이스 생성
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Workspace Name Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        워크스페이스명
                        <span 
                          className="ml-1"
                          style={{ 
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="워크스페이스명을 입력해주세요."
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        size="small"
                        className="w-full"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Key Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        순서
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="순서를 입력해주세요. (선택사항)"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        size="small"
                        className="w-full"
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              <Button
                color="light"
                size="medium"
                onClick={handleClose}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                취소
              </Button>
              <Button
                color="dark" 
                size="medium"
                onClick={handleSubmit}
                disabled={isLoading || !workspaceName.trim()}
                className="px-[10px] h-10"
              >
                {isLoading ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}